import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import {
  AddStudentDialog,
  ClassAssignmentPicker,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FixMissingDataDialog,
  InlineAlert,
  LoadingButton,
  SourceBadge,
  StatusChip,
} from "src/grading/components";
import type { PickedStaff } from "src/grading/components/ClassAssignmentPicker/ClassAssignmentPicker";
import type { DataTableColumn } from "src/grading/components/DataTable/DataTable";
import type { FixMissingTarget } from "src/grading/components/FixMissingDataDialog/FixMissingDataDialog";
import Loader from "src/grading/components/Loader/Loader";
import { initialsOf } from "src/grading/components/StudentCell/StudentCell";
import { usePermissions } from "src/grading/hooks/usePermissions";
import { PageHeader } from "src/grading/layout/PageHeader";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { RosterStudent } from "src/models";
import {
  useGetClassQuery,
  useGetClassTeachersQuery,
  useGetRosterQuery,
  useGetStudentQuery,
  useSetClassTeachersMutation,
  useWithdrawStudentMutation,
} from "src/store/reducers/classes/classesApiSlice";
import { useGetSchoolQuery } from "src/store/reducers/districts/districtsApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import AccommodationsPanel from "./AccommodationsPanel";

const LEVEL_SHORT: Record<string, string> = {
  modification: "Modification",
  adaptation: "Adaptation",
  alternative: "Alternative test",
};
const FLAG_LABEL: Record<string, string> = {
  asthma: "Asthma",
  cardiac: "Cardiac",
  injury: "Injury",
  other: "Other (see notes)",
};
const ageFor = (s: RosterStudent) => {
  if (s.ageOverride !== null) return s.ageOverride;
  if (!s.birthDate) return null;
  const b = new Date(s.birthDate);
  const n = new Date();
  let age = n.getFullYear() - b.getFullYear();
  const m = n.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && n.getDate() < b.getDate())) age -= 1;
  return age;
};
const fullName = (s: RosterStudent) => `${s.firstName} ${s.lastName}`;
const fixTarget = (s: RosterStudent): FixMissingTarget => ({
  id: s.id,
  firstName: s.firstName,
  lastName: s.lastName,
  missingSex: !s.benchmarkSex,
  missingAge: ageFor(s) === null,
});

/** Small rounded label used in roster cells (plan level, health flags, school-set table). */
const Pill = ({
  tone,
  children,
}: {
  tone: "participation" | "danger" | "neutral" | "warning";
  children: string;
}) => (
  <Box
    component="span"
    sx={(theme) => ({
      display: "inline-flex",
      alignItems: "center",
      px: 1.125,
      py: 0.625,
      borderRadius: `${radius.pill}px`,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1,
      whiteSpace: "nowrap",
      ...(tone === "participation"
        ? {
            bgcolor: theme.palette.tierParticipationBg,
            color: theme.palette.tierParticipationFg,
          }
        : tone === "danger"
          ? {
              bgcolor: theme.palette.dangerSoft,
              color: theme.palette.dangerInk,
            }
          : tone === "warning"
            ? {
                bgcolor: theme.palette.tokens.warningBg,
                color: theme.palette.tokens.warningFg,
              }
            : {
                bgcolor: "transparent",
                color: "text.primary",
                fontSize: 13,
                fontWeight: 600,
                px: 0,
              }),
    })}
  >
    {children}
  </Box>
);

/** One class: teachers of record, the roster with only the allowed columns, manual add, and gap fixes. */
const ClassDetailPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { classId } = useParams<{ classId: string }>();
  const { hasPermissions } = usePermissions();
  const { data: schoolClass, isLoading, isError } = useGetClassQuery(classId);
  // Only admins read schools; for a teacher the school (back link, subtitle) is left out.
  const canReadSchool =
    hasPermissions(
      permissions.READ_SCHOOLS.action,
      permissions.READ_SCHOOLS.subject,
    ) ||
    hasPermissions(
      permissions.READ_OWN_SCHOOL.action,
      permissions.READ_OWN_SCHOOL.subject,
    );
  const { data: school } = useGetSchoolQuery(schoolClass?.schoolId ?? "", {
    skip: !schoolClass || !canReadSchool,
  });
  const { data: students, isLoading: loadingRoster } =
    useGetRosterQuery(classId);
  const { data: teachers } = useGetClassTeachersQuery(classId);
  const canSetTeachers = hasPermissions(
    permissions.UPDATE_CLASSES.action,
    permissions.UPDATE_CLASSES.subject,
  );
  // Platform admins name the district on list searches; everyone else is scoped by the server.
  const isPlatformAdmin = hasPermissions(
    permissions.READ_DISTRICTS.action,
    permissions.READ_DISTRICTS.subject,
  );
  const [setTeachers, { isLoading: savingTeachers }] =
    useSetClassTeachersMutation();
  const [withdrawStudent, { isLoading: withdrawing }] =
    useWithdrawStudentMutation();

  const canAdd = hasPermissions(
    permissions.ADD_CLASS_STUDENTS.action,
    permissions.ADD_CLASS_STUDENTS.subject,
  );
  const canEditStudents = hasPermissions(
    permissions.UPDATE_SCHOOL_STUDENTS.action,
    permissions.UPDATE_SCHOOL_STUDENTS.subject,
  );
  // Staff with accommodation access (read-accommodations): gates the accommodation · health column and the drawer's plan.
  const canReadAccommodations = hasPermissions(
    permissions.READ_ACCOMMODATIONS.action,
    permissions.READ_ACCOMMODATIONS.subject,
  );
  const canEditAccommodations = hasPermissions(
    permissions.UPDATE_ACCOMMODATIONS.action,
    permissions.UPDATE_ACCOMMODATIONS.subject,
  );
  // Teachers withdraw from their own classes; admins from any class in scope (server re-checks).
  const canUpdateStudents =
    canEditStudents ||
    hasPermissions(
      permissions.UPDATE_CLASS_STUDENTS.action,
      permissions.UPDATE_CLASS_STUDENTS.subject,
    );
  const [recordFor, setRecordFor] = useState<string | null>(null);
  // The record's read-only fields come from the student detail (FR-022).
  const { data: recordDetail } = useGetStudentQuery(recordFor ?? "", {
    skip: !recordFor,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [fix, setFix] = useState<FixMissingTarget | null>(null);
  const [removing, setRemoving] = useState<RosterStudent | null>(null);
  const [rosterNotice, setRosterNotice] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);
  const [assigned, setAssigned] = useState<PickedStaff[]>([]);
  const [teacherError, setTeacherError] = useState<string | null>(null);

  const className = schoolClass?.name;
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({
        label: t("nav.classes", { defaultValue: "Classes" }),
        path: "/grading/classes",
      }),
    );
    if (className) dispatch(addBreadcrumb({ label: className }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, className, t]);
  useEffect(() => {
    if (teachers) {
      setAssigned(
        teachers.map((x) => ({
          profileId: x.profileId,
          name: `${x.firstName} ${x.lastName}`.trim(),
          email: x.email,
          pending: !x.registered,
          role: x.role ?? "teacher",
        })),
      );
    }
  }, [teachers]);

  const missing = useMemo(
    () => (students ?? []).filter((s) => !s.benchmarkSex || ageFor(s) === null),
    [students],
  );
  const recordStudent = useMemo(
    () => (students ?? []).find((s) => s.id === recordFor) ?? null,
    [students, recordFor],
  );
  const canFix = canAdd || canUpdateStudents;
  const rosterClass = schoolClass?.source === "roster";
  const canWithdraw = canUpdateStudents && !rosterClass;

  const openFix = (s: RosterStudent) => setFix(fixTarget(s));

  const withdraw = async () => {
    if (!removing || !schoolClass) return;
    const name = fullName(removing);
    setRosterNotice(null);
    try {
      await withdrawStudent({ studentId: removing.id, classId }).unwrap();
      setRosterNotice({
        severity: "success",
        text: t("student.removed", { name, className: schoolClass.name }),
      });
    } catch (e) {
      const code = refusalCode(e);
      setRosterNotice({
        severity: "error",
        text: code
          ? refusalCopy(t, code)
          : messageOf(e, t("student.removeError")),
      });
    }
    setRemoving(null);
  };

  const saveTeachers = async () => {
    setTeacherError(null);
    try {
      await setTeachers({
        classId,
        teachers: assigned.map(({ profileId, role }) => ({ profileId, role })),
      }).unwrap();
    } catch (e) {
      setTeacherError(
        messageOf(
          e,
          t("classes.updateTeachersError", {
            defaultValue: "Could not update teachers",
          }),
        ),
      );
    }
  };

  if (isLoading) return <Loader />;
  if (isError || !schoolClass)
    return (
      <InlineAlert severity="error">
        {t("classes.notFound", {
          defaultValue: "Class not found or outside your scope.",
        })}
      </InlineAlert>
    );

  const missingText = t("classes.missingValue", { defaultValue: "Missing" });
  const manualTooltip = t("classes.manualTooltip", {
    defaultValue: "Added by hand; the next roster sync matches it by SIS id",
  });
  const teacherNames = (teachers ?? [])
    .map((x) => `${x.firstName} ${x.lastName}`)
    .join(", ");
  const summary = [
    school?.name,
    schoolClass.gradeLevel
      ? t("classes.gradeLabel", {
          defaultValue: "Grade {{grade}}",
          grade: schoolClass.gradeLevel,
        })
      : undefined,
    schoolClass.schoolYear,
    teacherNames
      ? t("classes.teacherOfRecord", {
          defaultValue: "Teacher of record: {{names}}",
          names: teacherNames,
        })
      : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  const columns: DataTableColumn<RosterStudent>[] = [
    {
      key: "student",
      header: t("classes.student", { defaultValue: "Student" }),
      render: (s) => (
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
          <Box
            aria-hidden
            sx={{
              width: 30,
              height: 30,
              flex: "none",
              borderRadius: "50%",
              bgcolor: "primary.light",
              color: "primaryInk",
              display: "grid",
              placeItems: "center",
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            {initialsOf(s.firstName, s.lastName)}
          </Box>
          <Button
            variant="text"
            size="small"
            onClick={() => setRecordFor(s.id)}
            sx={{
              p: 0,
              minHeight: 0,
              minWidth: 0,
              fontSize: 14,
              fontWeight: 800,
              color: "text.primary",
              textAlign: "left",
            }}
          >
            {fullName(s)}
          </Button>
        </Box>
      ),
    },
    {
      key: "grade",
      header: t("classes.grade"),
      render: (s) => s.gradeLevel ?? "—",
    },
    {
      key: "age",
      header: t("classes.age"),
      render: (s) => {
        const age = ageFor(s);
        return age === null ? (
          <Pill tone="warning">{missingText}</Pill>
        ) : (
          <Box
            component="span"
            sx={{
              fontSize: 15,
              fontWeight: 700,
              fontStretch: fontStretch.condensed,
            }}
          >
            {age}
          </Box>
        );
      },
    },
    {
      key: "benchmark",
      header: t("classes.benchmarkTable"),
      render: (s) =>
        s.benchmarkSex ? (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Pill tone="neutral">{s.benchmarkSex}</Pill>
            {s.benchmarkSexSource === "school" && (
              <Typography
                component="span"
                sx={(theme) => ({
                  fontSize: 12,
                  color: theme.palette.tokens.textMuted,
                })}
              >
                {t("classes.setBySchool", { defaultValue: "school" })}
              </Typography>
            )}
          </Stack>
        ) : (
          <Pill tone="warning">{missingText}</Pill>
        ),
    },
    {
      key: "sis",
      header: t("classes.sisId", { defaultValue: "SIS id" }),
      render: (s) => (
        <Box component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
          {s.externalId ?? "—"}
        </Box>
      ),
    },
    {
      key: "source",
      header: t("classes.sourceLabel", { defaultValue: "Source" }),
      render: (s) => (
        <Box
          component="span"
          title={s.source === "manual" ? manualTooltip : undefined}
        >
          <SourceBadge source={s.source} />
        </Box>
      ),
    },
    ...(canReadAccommodations
      ? [
          {
            key: "accommodation",
            header: t("classes.accommodationHealth", {
              defaultValue: "Accommodation · health",
            }),
            render: (s: RosterStudent) => {
              const a = s.accommodation;
              const level =
                a && a.plannedModification !== "none"
                  ? a.plannedModification
                  : null;
              const flags = a?.healthFlags ?? [];
              if (!level && !flags.length) return "—";
              return (
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {level && (
                    <Pill tone="participation">
                      {t(`modification.short.${level}`, {
                        defaultValue: LEVEL_SHORT[level] ?? level,
                      })}
                    </Pill>
                  )}
                  {flags.length > 0 && (
                    <Pill tone="danger">
                      {flags
                        .map((f) =>
                          t(`classes.healthFlag.${f}`, {
                            defaultValue: FLAG_LABEL[f] ?? f,
                          }),
                        )
                        .join(", ")}
                    </Pill>
                  )}
                </Stack>
              );
            },
          },
        ]
      : []),
    ...(canFix || canWithdraw
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            render: (s: RosterStudent) => (
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {canFix && (!s.benchmarkSex || ageFor(s) === null) && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openFix(s)}
                    aria-label={`${t("common.fix")} ${fullName(s)}`}
                  >
                    {t("common.fix")}
                  </Button>
                )}
                {canUpdateStudents && (
                  <Button
                    size="small"
                    onClick={() => history.push(`/grading/students/${s.id}`)}
                    aria-label={`${t("classes.editStudent", { defaultValue: "Edit" })} ${fullName(s)}`}
                  >
                    {t("classes.editStudent", { defaultValue: "Edit" })}
                  </Button>
                )}
                {canWithdraw && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setRemoving(s)}
                    aria-label={t("student.removeLabel", { name: fullName(s) })}
                  >
                    {t("student.removeFromClass")}
                  </Button>
                )}
              </Stack>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title={schoolClass.name}
        backTo={school ? `/grading/schools/${school.id}` : undefined}
        backLabel={school?.name}
        description={
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {summary}
            {schoolClass.source === "manual" && (
              <Box component="span" title={manualTooltip}>
                <StatusChip status="manual" />
              </Box>
            )}
          </Box>
        }
        actions={
          <>
            {canAdd && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setAddOpen(true)}
              >
                {t("classes.addStudent")}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() =>
                history.push(`/grading/sessions/plan?classId=${classId}`)
              }
            >
              {t("sessions.plan")}
            </Button>
          </>
        }
      />

      {missing.length > 0 && (
        <InlineAlert
          severity="warning"
          banner
          title={
            missing.length === 1
              ? t("classes.missingOne", {
                  defaultValue: "{{name}} can't be scored yet.",
                  name: fullName(missing[0]),
                })
              : t("classes.missingMany", {
                  defaultValue:
                    "{{count}} students can't be scored yet: {{names}}.",
                  count: missing.length,
                  names: missing.map(fullName).join(", "),
                })
          }
          action={
            canFix ? (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => openFix(missing[0])}
              >
                {t("common.fix")}
              </Button>
            ) : undefined
          }
        >
          {t("classes.missingBody", {
            defaultValue:
              "The roster didn't include a benchmark table or age. Set it as the school has recorded it.",
          })}
        </InlineAlert>
      )}

      <Paper component="section" aria-labelledby="class-roster">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ px: 2.75, py: 2 }}
        >
          <Typography id="class-roster" variant="h5" component="h2">
            {t("classes.rosterCount", {
              defaultValue: "Roster · {{count}}",
              count: students?.length ?? 0,
            })}
          </Typography>
          <Typography
            sx={(theme) => ({
              fontSize: 12.5,
              fontWeight: 600,
              color: theme.palette.tokens.textMuted,
            })}
          >
            {t("classes.minimalFields", {
              defaultValue:
                "Only the fields the test needs. No email, no body measurements.",
            })}
          </Typography>
        </Stack>
        {rosterClass && canUpdateStudents ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 2.75, pb: 1.5 }}
          >
            <SourceBadge source="roster" />
            <Typography sx={{ fontSize: 13 }}>
              {t("student.rosterClassNote")}
            </Typography>
          </Stack>
        ) : null}
        {rosterNotice ? (
          <Box sx={{ px: 2.75, pb: 1.5 }}>
            <InlineAlert severity={rosterNotice.severity}>
              {rosterNotice.text}
            </InlineAlert>
          </Box>
        ) : null}
        <DataTable
          aria-label={t("classes.roster")}
          columns={columns}
          rows={students ?? []}
          getRowKey={(s) => s.id}
          loading={loadingRoster}
          dense
          minWidth={840}
          emptyState={
            <EmptyState headingLevel={3} title={t("classes.noStudents")} />
          }
        />
      </Paper>

      <Paper
        component="section"
        aria-labelledby="class-teachers"
        sx={{ p: 2.75 }}
      >
        <Typography
          id="class-teachers"
          variant="h4"
          component="h2"
          sx={{ mb: 1.5 }}
        >
          {t("classes.teachersOfRecord", {
            defaultValue: "Teachers of record",
          })}
        </Typography>
        {canSetTeachers ? (
          <Stack spacing={2}>
            <ClassAssignmentPicker
              mode="byClass"
              label={t("classes.teachers", { defaultValue: "Teachers" })}
              districtId={isPlatformAdmin ? schoolClass.districtId : undefined}
              schoolId={schoolClass.schoolId}
              value={assigned}
              onChange={setAssigned}
              helperText={t("classes.teachersHelper", {
                defaultValue:
                  "Teachers see this class and its sessions in the app and console",
              })}
            />
            {teacherError && (
              <InlineAlert severity="error">{teacherError}</InlineAlert>
            )}
            <Box>
              <LoadingButton
                variant="outlined"
                loading={savingTeachers}
                onClick={saveTeachers}
                label={t("classes.saveTeachers", {
                  defaultValue: "Save teachers",
                })}
              />
            </Box>
          </Stack>
        ) : (
          <Typography>
            {(teachers ?? [])
              .map(
                (x) =>
                  `${x.firstName} ${x.lastName}${x.registered ? "" : ` ${t("classes.notSignedInYet", { defaultValue: "(not signed in yet)" })}`}`,
              )
              .join(", ") ||
              t("classes.noTeachers", {
                defaultValue: "No teachers assigned.",
              })}
          </Typography>
        )}
      </Paper>

      <AddStudentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        classId={classId}
        defaultGrade={schoolClass.gradeLevel}
      />

      <AccommodationsPanel
        student={recordStudent}
        readOnlyFields={
          recordDetail && recordDetail.id === recordFor
            ? recordDetail.readOnlyFields
            : undefined
        }
        classId={classId}
        classLabel={schoolClass.name}
        canViewHealth={canReadAccommodations}
        canEdit={canEditAccommodations}
        onClose={() => setRecordFor(null)}
      />

      <FixMissingDataDialog
        student={fix}
        classId={classId}
        onClose={() => setFix(null)}
      />

      <ConfirmDialog
        open={!!removing}
        tone="destructive"
        title={t("student.removeTitle", {
          name: removing ? fullName(removing) : "",
          className: schoolClass.name,
        })}
        body={t("student.removeBody")}
        confirmLabel={t("student.removeFromClass")}
        loading={withdrawing}
        onConfirm={withdraw}
        onClose={() => setRemoving(null)}
      />
    </Box>
  );
};

export default ClassDetailPage;
